import React, { useEffect, useState } from "react";
import {
  User,
  Mail,
  Phone,
  Droplet,
  Ruler,
  Calendar,
  Shield,
  Lock,
  Eye,
  EyeOff,
  Save,
  Send,
} from "lucide-react";
import Swal from "sweetalert2";
import { authHeaders } from "../lib/auth";
import { getDepartments, getRoles } from "../lib/api";

const toDateInput = (v: string | null | undefined): string => {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  safety_man: "Safety Man",
  safety_officer: "Safety Officer",
  hrd: "HRD",
  employee: "Employee",
};

interface ProfileData {
  id: string;
  name: string;
  department: string;
  email: string;
  phone: string;
  blood_type: string;
  birth_date: string;
  gender: "male" | "female" | "";
  height: number | null;
  status: string;
  telegram_chat_id?: string | null;
  username: string;
  user_role: string;
}

export const Profile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [roleLabels, setRoleLabels] =
    useState<Record<string, string>>(ROLE_LABELS);
  const [departmentLabels, setDepartmentLabels] = useState<
    Record<string, string>
  >({});

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    blood_type: "",
    gender: "",
    height: "",
    birth_date: "",
    telegram_chat_id: "",
  });
  const [pwd, setPwd] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    getRoles()
      .then((roles) => {
        setRoleLabels(
          Object.fromEntries(roles.map((role) => [role.name, role.label])),
        );
      })
      .catch((err) => console.error("Gagal memuat roles:", err));
    getDepartments()
      .then((departments) => {
        setDepartmentLabels(
          Object.fromEntries(
            departments.map((dept) => [dept.name, dept.label]),
          ),
        );
      })
      .catch((err) => console.error("Gagal memuat departemen:", err));
  }, []);

  useEffect(() => {
    let live = true;

    fetch(`${import.meta.env.VITE_API_BASE}/api/profile`, {
      cache: "no-store",
      headers: authHeaders(),
    })
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || "Gagal memuat profil");
        }
        return r.json();
      })
      .then((d: ProfileData) => {
        if (!live) return;
        setProfile(d);
        setForm({
          name: d.name || "",
          email: d.email || "",
          phone: d.phone || "",
          blood_type: d.blood_type || "",
          gender: d.gender || "",
          height: d.height != null ? String(d.height) : "",
          birth_date: toDateInput(d.birth_date),
          telegram_chat_id: d.telegram_chat_id || "",
        });
      })
      .catch((err) => {
        console.error("Profile fetch error:", err);
        Swal.fire({
          icon: "error",
          title: "Gagal Memuat Profil",
          text: err.message,
          confirmButtonText: "Tutup",
        });
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const payload: Record<string, string> = {
      name: form.name,
      email: form.email || "",
      phone: form.phone || "",
      blood_type: form.blood_type || "",
      gender: form.gender || "",
      height: form.height || "",
      birth_date: form.birth_date || "",
      telegram_chat_id: form.telegram_chat_id || "",
    };

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/api/profile`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Gagal menyimpan data");
      }

      const updated = await res.json();

      setForm((f) => ({
        ...f,
        birth_date: toDateInput(updated.birth_date) || f.birth_date,
      }));

      try {
        const user = JSON.parse(localStorage.getItem("user") || "null");
        if (user) {
          user.name = updated.name || user.name;
          localStorage.setItem("user", JSON.stringify(user));
        }
      } catch {
        // ignore storage errors
      }

      await Swal.fire({
        icon: "success",
        title: "Berhasil!",
        text: "Data profil berhasil diperbarui",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Profile update error:", err);
      Swal.fire({
        icon: "error",
        title: "Gagal!",
        text: (err as Error).message,
        confirmButtonText: "Tutup",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pwd.current_password || !pwd.new_password) {
      Swal.fire({
        icon: "warning",
        title: "Lengkapi Form",
        text: "Password saat ini dan password baru wajib diisi",
      });
      return;
    }

    if (pwd.new_password.length < 6) {
      Swal.fire({
        icon: "warning",
        title: "Password Terlalu Pendek",
        text: "Password baru minimal 6 karakter",
      });
      return;
    }

    if (pwd.new_password !== pwd.confirm_password) {
      Swal.fire({
        icon: "warning",
        title: "Konfirmasi Tidak Cocok",
        text: "Konfirmasi password tidak sama dengan password baru",
      });
      return;
    }

    setSaving(true);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/api/profile`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            current_password: pwd.current_password,
            new_password: pwd.new_password,
          }),
        },
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Gagal mengubah password");
      }

      setPwd({ current_password: "", new_password: "", confirm_password: "" });

      await Swal.fire({
        icon: "success",
        title: "Berhasil!",
        text: "Password berhasil diubah",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Password change error:", err);
      Swal.fire({
        icon: "error",
        title: "Gagal!",
        text: (err as Error).message,
        confirmButtonText: "Tutup",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 lg:pt-0">
        <div className="p-4 lg:pr-14 lg:pb-4 sm:pt-6 lg:pt-8">
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-48 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-10 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 lg:pt-0 flex items-center justify-center">
        <div className="text-center">
          <User className="h-16 w-16 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 text-lg">
            Profil karyawan tidak ditemukan
          </p>
        </div>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div className="min-h-screen bg-gray-50 pt-16 lg:pt-0">
      <div className="p-4 lg:pr-14 lg:pb-4 sm:pt-6 lg:pt-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Profil Saya
          </h1>
          <p className="text-gray-600">
            Kelola data pribadi dan ubah password akun Anda
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ── Info summary card ── */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="bg-blue-100 p-4 rounded-full mb-4">
                  <User className="h-10 w-10 text-blue-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  {profile.name}
                </h2>
                <p className="text-sm text-gray-500">
                  {roleLabels[profile.user_role] || profile.user_role}
                </p>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Username</span>
                  <span className="font-medium text-gray-900">
                    {profile.username}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Divisi</span>
                  <span className="font-medium text-gray-900">
                    {departmentLabels[profile.department] ||
                      profile.department}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Status</span>
                  <span
                    className={`font-medium ${
                      profile.status === "active"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {profile.status === "active" ? "Aktif" : "Tidak Aktif"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Edit forms ── */}
          <div className="lg:col-span-2 space-y-8">
            {/* Data pribadi */}
            <form
              onSubmit={handleSave}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-blue-600" />
                Data Pribadi
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nama Lengkap
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) =>
                      setForm({ ...form, name: e.target.value })
                    }
                    required
                    placeholder="Nama lengkap"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                      placeholder="email@example.com"
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    No Telp
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                      placeholder="08xxxxxxxxxx"
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Golongan Darah
                  </label>
                  <div className="relative">
                    <Droplet className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <select
                      value={form.blood_type}
                      onChange={(e) =>
                        setForm({ ...form, blood_type: e.target.value })
                      }
                      className={`${inputClass} pl-10`}
                    >
                      <option value="">Pilih Golongan Darah</option>
                      {["A", "A+", "A-", "B", "B+", "B-", "AB", "AB+", "AB-", "O", "O+", "O-"].map(
                        (b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jenis Kelamin
                  </label>
                  <select
                    value={form.gender}
                    onChange={(e) =>
                      setForm({ ...form, gender: e.target.value })
                    }
                    className={inputClass}
                  >
                    <option value="">Pilih Jenis Kelamin</option>
                    <option value="male">Laki-laki</option>
                    <option value="female">Perempuan</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tanggal Lahir
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="date"
                      value={form.birth_date}
                      onChange={(e) =>
                        setForm({ ...form, birth_date: e.target.value })
                      }
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tinggi Badan
                  </label>
                  <div className="relative">
                    <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.1"
                      min={100}
                      max={250}
                      value={form.height}
                      onChange={(e) =>
                        setForm({ ...form, height: e.target.value })
                      }
                      placeholder="170"
                      className={`${inputClass} pl-10 pr-12`}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                      cm
                    </span>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telegram Chat ID
                  </label>
                  <div className="relative">
                    <Send className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={form.telegram_chat_id}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          telegram_chat_id: e.target.value,
                        })
                      }
                      placeholder="123456789"
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Notifikasi hasil kesehatan akan disend ke akun Telegram ini.
                    Chat ID didapat dari bot @userinfobot.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="mt-8 w-full sm:w-auto bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <Save className="h-5 w-5 mr-2" />
                {saving ? "Menyimpan..." : "Simpan Data"}
              </button>
            </form>

            {/* Ganti password */}
            <form
              onSubmit={handleChangePassword}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Lock className="h-5 w-5 mr-2 text-blue-600" />
                Ubah Password
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password Saat Ini
                  </label>
                  <input
                    type="password"
                    value={pwd.current_password}
                    onChange={(e) =>
                      setPwd({
                        ...pwd,
                        current_password: e.target.value,
                      })
                    }
                    placeholder="Password saat ini"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password Baru
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={pwd.new_password}
                      onChange={(e) =>
                        setPwd({ ...pwd, new_password: e.target.value })
                      }
                      placeholder="Minimal 6 karakter"
                      className={`${inputClass} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-500 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Konfirmasi Password Baru
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={pwd.confirm_password}
                    onChange={(e) =>
                      setPwd({
                        ...pwd,
                        confirm_password: e.target.value,
                      })
                    }
                    placeholder="Ulangi password baru"
                    className={inputClass}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="mt-8 w-full sm:w-auto bg-gray-800 text-white py-3 px-8 rounded-lg hover:bg-gray-900 focus:ring-4 focus:ring-gray-200 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <Lock className="h-5 w-5 mr-2" />
                {saving ? "Menyimpan..." : "Ubah Password"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
